import { useEffect, useState } from "react";
import { toast } from "sonner";
import Markdown from "react-markdown";
import { api, errMsg } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { AcceptanceReport } from "@/components/AcceptanceReport";

function Editor({ kind, title }) {
  const [doc, setDoc] = useState({ body: "", effective_date: "" });
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const [sig, setSig] = useState({ on: false, summary: "" });

  useEffect(() => {
    api.get(`/admin/legal/${kind}`).then(({ data }) => { setDoc({ body: data.body, effective_date: data.effective_date, updated_at: data.updated_at, version: data.version || 1, notice: data.notice }); setLoaded(true); })
      .catch((e) => toast.error(errMsg(e)));
  }, [kind]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/legal/${kind}`, { body: doc.body, effective_date: doc.effective_date, significant: sig.on, change_summary: sig.summary });
      setDoc({ ...doc, updated_at: data.updated_at, version: data.version, notice: data.notice });
      toast.success(sig.on ? `${title} v${data.version} published — artists will be asked to re-accept, and a notice email is being sent` : `${title} published`);
      setSig({ on: false, summary: "" });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-[#E6DBCF] bg-[#FDFBF7] p-6" data-testid={`admin-legal-${kind}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-2xl font-semibold text-[#2C1810]">{title}</h3>
        <p className="text-xs text-[#A88B7D]">{doc.updated_at ? `Version ${doc.version || 1} · last published ${doc.updated_at.slice(0, 10)}` : "Placeholder live — not yet published"}</p>
      </div>
      {doc.notice && (
        <p className="mt-2 text-xs text-[#6E5549]" data-testid={`admin-legal-${kind}-notice`}>
          Notice email for v{doc.notice.version}: {doc.notice.status === "sending" ? "sending" : doc.notice.status} · {doc.notice.sent} sent{doc.notice.failed ? `, ${doc.notice.failed} failed` : ""} of {doc.notice.total} artists
          <button onClick={() => api.get(`/admin/legal/${kind}`).then(({ data }) => setDoc((d) => ({ ...d, notice: data.notice })))} data-testid={`admin-legal-${kind}-notice-refresh`} className="ml-2 font-semibold text-[#9C6218] underline">Refresh</button>
        </p>
      )}
      <label className="mt-4 block text-[11px] font-semibold uppercase tracking-[0.18em] text-[#6E5549]">Effective date
        <Input data-testid={`admin-legal-${kind}-date`} value={doc.effective_date} onChange={(e) => setDoc({ ...doc, effective_date: e.target.value })} placeholder="e.g. November 1, 2026" className="mt-1.5 h-11 rounded-xl border-[#E6DBCF] text-sm normal-case tracking-normal" />
      </label>
      <div className="mt-4 flex gap-2">
        {["Edit", "Preview"].map((t) => (
          <button key={t} onClick={() => setPreview(t === "Preview")} data-testid={`admin-legal-${kind}-${t.toLowerCase()}-tab`}
            className={`min-h-[40px] rounded-full px-4 text-xs font-semibold ${preview === (t === "Preview") ? "bg-[#2C1810] text-[#FDFBF7]" : "border border-[#E6DBCF] text-[#2C1810]"}`}>{t}</button>
        ))}
      </div>
      {preview ? (
        <div className="ig-prose mt-3 max-h-[480px] overflow-y-auto rounded-xl border border-[#E6DBCF] bg-white p-5 text-sm text-[#4A3B32]"><Markdown>{doc.body || "*Nothing to preview yet.*"}</Markdown></div>
      ) : (
        <Textarea data-testid={`admin-legal-${kind}-body`} disabled={!loaded} value={doc.body} onChange={(e) => setDoc({ ...doc, body: e.target.value })} rows={16}
          placeholder={"Use {{APP_URL}} for the app address. Paste the final, approved text. Markdown supported, e.g.\n## 1. Agreement and operator\nThese Terms govern…"} className="mt-3 rounded-xl border-[#E6DBCF] font-mono-ig text-xs" />
      )}
      <div className="mt-4 rounded-xl border border-[#E8BDB5] bg-[#FDF2F0] p-4">
        <label className="flex cursor-pointer items-start gap-3">
          <Checkbox data-testid={`admin-legal-${kind}-significant`} checked={sig.on} onCheckedChange={(v) => setSig({ ...sig, on: !!v })} className="mt-0.5 h-5 w-5 data-[state=checked]:bg-[#2C1810]" />
          <span className="text-sm text-[#2C1810]"><span className="font-semibold">Significant change</span> — ask every artist to review and re-accept on their next visit, and email them your summary. Leave unticked for typo fixes and minor edits.</span>
        </label>
        {sig.on && (
          <Textarea data-testid={`admin-legal-${kind}-summary`} value={sig.summary} onChange={(e) => setSig({ ...sig, summary: e.target.value })} rows={3} maxLength={600}
            placeholder="Short, plain-language summary of what changed (shown to artists)" className="mt-3 rounded-xl border-[#E6DBCF] bg-white text-sm" />
        )}
      </div>
      <button onClick={save} disabled={busy || !loaded || (sig.on && !sig.summary.trim())} data-testid={`admin-legal-${kind}-save`} className="mt-4 min-h-[48px] rounded-full bg-[#2C1810] px-7 text-sm font-semibold text-[#FDFBF7] hover:bg-[#3D2317] disabled:opacity-50">
        {busy ? "Publishing…" : `Publish ${title}`}
      </button>
    </div>
  );
}

export function LegalEditor() {
  return (
    <div className="space-y-6">
      <p className="text-sm text-[#6E5549]">Publish only final, legally reviewed text. Leaving a document empty keeps the clearly marked placeholder in place.</p>
      <AcceptanceReport />
      <Editor kind="terms" title="Terms of Use" />
      <Editor kind="privacy" title="Privacy Policy" />
    </div>
  );
}
